import React from 'react';
import Alert from '@mui/material/Alert/Alert';
import HowToVoteIcon from '@mui/icons-material/HowToVote';
import { useLiveBoard } from '../../hooks/useLiveSessions';
import { HackathonPayload } from '../../services/hackathon.service';
import { participantsScore } from '../../utils/sessions';

/**
 * While voting is open, tells participants what it means for them: either that only the judges score,
 * or where to score the other teams. Judges and organizers have their own tools.
 * `where` picks the wording for the page it sits on: the hackathon page points at the idea pages; an idea page
 * already holds the vote panel when participants score, so there it only speaks up for judges-only voting.
 */
export const ParticipantVotingNotice = ({ hackathonId, hackathon, hidden, where = 'hackathon' }: {
    hackathonId: string,
    hackathon: HackathonPayload,
    hidden: boolean,
    where?: 'hackathon' | 'idea'
}) => {
    // use the same key as the rest of the page, so the page runs one poller, not two;
    // the live board is polled, so this follows the organizer's switch without a reload
    const { votingPeriodOn } = useLiveBoard(hackathonId);
    if (hidden || !votingPeriodOn || hackathon.winners?.length) {
        return null;
    }
    // judges-only categories everywhere count as "only the judges score": participants get nothing to do
    const scoring = participantsScore(hackathon);
    if (where === 'idea' && scoring) {
        return null;
    }
    return (
        <Alert severity="info" icon={<HowToVoteIcon />}>
            {
                scoring
                    ? 'Voting is open. Score the other teams from their idea pages below (you can\'t score your own team). The results will be announced here.'
                    : 'Voting is open. In this hackathon only the judges score the teams, so there\'s nothing for you to do. The results will be announced on the hackathon page.'
            }
        </Alert>
    );
}
